import React, { useState, useEffect } from 'react';
import rbacApi from '../../api/rbacApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Search,
  FilterList,
  Person,
  Download,
  AdminPanelSettings,
  Group,
  Folder,
  Security,
  Email,
  Send,
  Refresh,
  CheckCircle,
  Warning,
  Error,
  Info,
  Timeline,
  History,
  AccessTime,
  Notifications,
  NotificationsOff,
  Lock,
  LockOpen,
  Public,
  CalendarToday,
  ExpandMore,
  Visibility,
  VisibilityOff,
  Cancel
} from '@mui/icons-material';
import { dsVar, ink } from '../../theme/tokens';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`invitation-tabpanel-${index}`}
      aria-labelledby={`invitation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface Invitation {
  _id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  userGroupId?: string;
  projectId?: string;
  permissions: string[];
  message?: string;
  organisationName?: string;
}

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'accepted': return 'success';
    case 'rejected': return 'error';
    case 'expired': return 'default';
    default: return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending': return <AccessTime />;
    case 'accepted': return <CheckCircle />;
    case 'rejected': return <Error />;
    case 'expired': return <Warning />;
    default: return <Info />;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'organisation_admin': return 'error';
    case 'user_group_admin': return 'primary';
    case 'user': return 'success';
    case 'admin': return 'error';
    case 'manager': return 'warning';
    case 'viewer': return 'info';
    default: return 'default';
  }
};

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'organisation_admin':
    case 'admin':
      return <AdminPanelSettings />;
    case 'user_group_admin':
    case 'manager':
      return <Group />;
    case 'user':
    case 'viewer':
      return <Person />;
    default:
      return <Person />;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const isExpired = (expiresAt: string) => {
  return new Date(expiresAt) < new Date();
};

interface InvitationManagementProps {
  canSendInvitations?: boolean;
}

const InvitationManagement: React.FC<InvitationManagementProps> = ({ 
  canSendInvitations = true 
}) => {
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [sendInvitationDialog, setSendInvitationDialog] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  
  // Form states
  const [invitationForm, setInvitationForm] = useState({
    email: '',
    role: 'user',
    userGroupId: '',
    projectId: '',
    // Organization-level permissions
    orgPermissions: {
      canManageUsers: false,
      canInviteCrossGroupUsers: false,
      canAddCrossGroupUsers: false,
      canManageProjectUsers: false,
      canEditProjectSettings: false
    },
    // Legacy permissions array (for backward compatibility)
    permissions: [] as string[],
    message: '',
    organisationName: localStorage.getItem('org_id') || 'bctglobal'
  });
  
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [availableProjects, setAvailableProjects] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);

  // Load invitations from backend
  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch ALL invitations unless a specific filter is selected
      const response = filterStatus === 'all' 
        ? await rbacApi.getInvitations()
        : await rbacApi.getInvitationsByStatus(filterStatus);
      
      if (response && response.invitations) {
        setInvitations(response.invitations);
      } else if (response && Array.isArray(response)) {
        setInvitations(response);
      } else {
        setInvitations([]);
      }
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError('Failed to load invitations.');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  // Load available groups and projects
  const loadAvailableData = async () => {
    setLoadingGroups(true);
    setLoadingProjects(true);
    
    try {
      const [groupsResponse, projectsResponse] = await Promise.all([
        rbacApi.getGroups(),
        rbacApi.getProjects()
      ]);
      
      if (groupsResponse && Array.isArray(groupsResponse)) {
        setAvailableGroups(groupsResponse);
      } else if (groupsResponse && groupsResponse.groups) {
        setAvailableGroups(groupsResponse.groups);
      }
      
      if (projectsResponse && Array.isArray(projectsResponse)) {
        setAvailableProjects(projectsResponse);
      } else if (projectsResponse && projectsResponse.projects) {
        setAvailableProjects(projectsResponse.projects);
      }
    } catch (err) {
      console.error('Error loading available data:', err);
    } finally {
      setLoadingGroups(false);
      setLoadingProjects(false);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadInvitations();
    loadAvailableData();
  }, [filterStatus]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, invitation: Invitation) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedInvitation(invitation);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedInvitation(null);
  };

  const handleSendInvitation = async () => {
    try {
      setSendingInvitation(true);
      setError(null);
      
      const invitationData = {
        email: invitationForm.email,
        role: invitationForm.role,
        userGroupId: invitationForm.userGroupId || undefined,
        projectId: invitationForm.projectId || undefined,
        // Include both organization-level permissions and legacy permissions
        orgPermissions: invitationForm.orgPermissions,
        permissions: invitationForm.permissions,
        message: invitationForm.message,
        organisationName: invitationForm.organisationName
      };

      const response = await rbacApi.sendInvitation(invitationData);
      
      if (response && response.message) {
        setSuccess('Invitation sent successfully!');
        setSendInvitationDialog(false);
        setInvitationForm({
          email: '',
          role: 'user',
          userGroupId: '',
          projectId: '',
          orgPermissions: {
            canManageUsers: false,
            canInviteCrossGroupUsers: false,
            canAddCrossGroupUsers: false,
            canManageProjectUsers: false,
            canEditProjectSettings: false
          },
          permissions: [],
          message: '',
          organisationName: localStorage.getItem('org_id') || 'bctglobal'
        });
        loadInvitations(); // Refresh the list
      }
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      setError(err.message || 'Failed to send invitation.');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      setError(null);
      
      const response = await rbacApi.resendInvitation(
        invitation._id, 
        invitationForm.organisationName
      );
      
      if (response && response.message) {
        setSuccess('Invitation resent successfully!');
        handleMenuClose();
        loadInvitations(); // Refresh the list
      }
    } catch (err: any) {
      console.error('Error resending invitation:', err);
      setError(err.message || 'Failed to resend invitation.');
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    try {
      setError(null);
      
      const response = await rbacApi.cancelInvitation(
        invitation._id,
        localStorage.getItem('user_email') || 'admin@example.com'
      );
      
      if (response && response.message) {
        setSuccess('Invitation cancelled successfully!');
        handleMenuClose();
        loadInvitations(); // Refresh the list
      }
    } catch (err: any) {
      console.error('Error cancelling invitation:', err);
      setError(err.message || 'Failed to cancel invitation.');
    }
  };

  const filteredInvitations = invitations.filter(invitation => {
    const matchesSearch = invitation.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || invitation.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const acceptedInvitations = invitations.filter(inv => inv.status === 'accepted');
  const expiredInvitations = invitations.filter(inv => inv.status === 'expired' || isExpired(inv.expiresAt));

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: dsVar('page'), minHeight: '100vh', color: dsVar('fg') }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: dsVar('fg'), mb: 1, letterSpacing: '-0.02em' }}>
            Invitation Management
          </Typography>
          <Typography variant="body1" sx={{ color: dsVar('fg-2') }}>
            Invite new users and manage pending organization invitations.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setSendInvitationDialog(true)}
          disabled={!canSendInvitations}
          sx={{
            bgcolor: dsVar('accent'),
            color: dsVar('on-accent'),
            fontWeight: 700,
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': { bgcolor: dsVar('accent'), transform: 'translateY(-2px)' },
            transition: 'all 0.2s ease-in-out',
            boxShadow: '0 4px 14px 0 rgba(255, 222, 89, 0.39)',
            '&.Mui-disabled': { bgcolor: dsVar('line'), color: dsVar('fg-2') }
          }}
        >
          Invite New User
        </Button>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ borderBottom: `1px solid ${dsVar('line')}`, mb: 4 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { 
              color: dsVar('fg-2'), 
              textTransform: 'none', 
              fontWeight: 600,
              fontSize: '0.95rem',
              minWidth: 120,
              '&.Mui-selected': { color: dsVar('accent-fg') }
            },
            '& .MuiTabs-indicator': { bgcolor: dsVar('accent'), height: '3px', borderRadius: '3px 3px 0 0' }
          }}
        >
          <Tab label={
            <Box display="flex" alignItems="center" gap={1}>
              All <Chip label={invitations.length} size="small" sx={{ height: 20, bgcolor: dsVar('fg-2', 0.1), color: 'inherit', fontSize: '0.7rem' }} />
            </Box>
          } />
          <Tab label={
            <Box display="flex" alignItems="center" gap={1}>
              Pending <Chip label={pendingInvitations.length} size="small" sx={{ height: 20, bgcolor: 'rgba(255, 222, 89, 0.1)', color: 'inherit', fontSize: '0.7rem' }} />
            </Box>
          } />
          <Tab label={
            <Box display="flex" alignItems="center" gap={1}>
              Accepted <Chip label={acceptedInvitations.length} size="small" sx={{ height: 20, bgcolor: 'rgba(16, 185, 129, 0.1)', color: 'inherit', fontSize: '0.7rem' }} />
            </Box>
          } />
          <Tab label={
            <Box display="flex" alignItems="center" gap={1}>
              Expired <Chip label={expiredInvitations.length} size="small" sx={{ height: 20, bgcolor: 'rgba(239, 68, 68, 0.1)', color: 'inherit', fontSize: '0.7rem' }} />
            </Box>
          } />
        </Tabs>
      </Box>

      {/* Search and Filter */}
      <Card sx={{ bgcolor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: '16px', mb: 4, backgroundImage: 'none' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                placeholder="Search by email address..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: dsVar('accent-fg') }} />
                    </InputAdornment>
                  ),
                  sx: {
                    color: dsVar('fg'),
                    bgcolor: dsVar('page'),
                    borderRadius: '12px',
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } }}>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{
                    color: dsVar('fg'),
                    bgcolor: dsVar('page'),
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') },
                    '& .MuiSvgIcon-root': { color: dsVar('fg-2') }
                  }}
                  MenuProps={{
                    PaperProps: { sx: { bgcolor: dsVar('surface'), color: dsVar('fg'), border: `1px solid ${dsVar('line')}` } }
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <IconButton onClick={loadInvitations} sx={{ color: dsVar('accent-fg'), bgcolor: 'rgba(255, 222, 89, 0.05)', '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.1)' } }}>
                <Refresh />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, mx: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, mx: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <InvitationsTable 
          invitations={filteredInvitations}
          loading={loading}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuAnchorEl={menuAnchorEl}
          selectedInvitation={selectedInvitation}
          onResend={handleResendInvitation}
          onCancel={handleCancelInvitation}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <InvitationsTable 
          invitations={pendingInvitations.filter(inv => 
            inv.email.toLowerCase().includes(searchTerm.toLowerCase())
          )}
          loading={loading}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuAnchorEl={menuAnchorEl}
          selectedInvitation={selectedInvitation}
          onResend={handleResendInvitation}
          onCancel={handleCancelInvitation}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <InvitationsTable 
          invitations={acceptedInvitations.filter(inv => 
            inv.email.toLowerCase().includes(searchTerm.toLowerCase())
          )}
          loading={loading}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuAnchorEl={menuAnchorEl}
          selectedInvitation={selectedInvitation}
          onResend={handleResendInvitation}
          onCancel={handleCancelInvitation}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <InvitationsTable 
          invitations={expiredInvitations.filter(inv => 
            inv.email.toLowerCase().includes(searchTerm.toLowerCase())
          )}
          loading={loading}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuAnchorEl={menuAnchorEl}
          selectedInvitation={selectedInvitation}
          onResend={handleResendInvitation}
          onCancel={handleCancelInvitation}
        />
      </TabPanel>

      {/* Send Invitation Dialog */}
      <SendInvitationDialog
        open={sendInvitationDialog}
        onClose={() => setSendInvitationDialog(false)}
        form={invitationForm}
        setForm={setInvitationForm}
        onSubmit={handleSendInvitation}
        loading={sendingInvitation}
        availableGroups={availableGroups}
        availableProjects={availableProjects}
        loadingGroups={loadingGroups}
        loadingProjects={loadingProjects}
      />

    
    </Box>
  );
};

// Invitations Table Component
interface InvitationsTableProps {
  invitations: Invitation[];
  loading: boolean;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, invitation: Invitation) => void;
  onMenuClose: () => void;
  menuAnchorEl: HTMLElement | null;
  selectedInvitation: Invitation | null;
  onResend: (invitation: Invitation) => void;
  onCancel: (invitation: Invitation) => void;
}

const InvitationsTable: React.FC<InvitationsTableProps> = ({
  invitations,
  loading,
  onMenuOpen,
  onMenuClose,
  menuAnchorEl,
  selectedInvitation,
  onResend,
  onCancel
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (invitations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="body1" color="text.secondary">
          No invitations found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ bgcolor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: '16px', backgroundImage: 'none', overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: dsVar('line', 0.5) }}>
            <TableRow>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Email Address</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Assigned Role</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Invitation Status</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Created By</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Sent Date</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Expiry Date</TableCell>
              <TableCell align="right" sx={{ color: dsVar('accent-fg'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((invitation) => (
              <TableRow key={invitation._id} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.02)' }, '& td': { borderBottom: `1px solid ${dsVar('line')}` } }}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: dsVar('line'), color: dsVar('accent-fg') }}>
                      {invitation.email.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" sx={{ color: dsVar('fg'), fontWeight: 600 }}>{invitation.email}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={React.cloneElement(getRoleIcon(invitation.role) as React.ReactElement, { sx: { color: 'inherit !important', fontSize: '1rem' } })}
                    label={invitation.role.replace(/_/g, ' ')}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(56, 189, 248, 0.1)', 
                      color: ink('#38bdf8'), 
                      fontWeight: 600,
                      borderRadius: '6px',
                      '& .MuiChip-icon': { color: 'inherit' }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={React.cloneElement(getStatusIcon(invitation.status) as React.ReactElement, { sx: { color: 'inherit !important', fontSize: '1rem' } })}
                    label={invitation.status.toUpperCase()}
                    size="small"
                    sx={{ 
                      bgcolor: invitation.status === 'accepted' ? 'rgba(16, 185, 129, 0.1)' : 
                               invitation.status === 'pending' ? 'rgba(255, 222, 89, 0.1)' : 
                               invitation.status === 'rejected' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                      color: invitation.status === 'accepted' ? ink('#10b981') : 
                             invitation.status === 'pending' ? dsVar('accent-fg') : 
                             invitation.status === 'rejected' ? ink('#ef4444') : dsVar('fg-2'),
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      '& .MuiChip-icon': { color: 'inherit' }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: dsVar('fg-2'), fontSize: '0.875rem' }}>{invitation.invitedBy}</TableCell>
                <TableCell sx={{ color: dsVar('fg-2'), fontSize: '0.875rem' }}>{formatDate(invitation.createdAt)}</TableCell>
                <TableCell sx={{ color: isExpired(invitation.expiresAt) ? ink('#ef4444') : dsVar('fg-2'), fontSize: '0.875rem' }}>
                  {formatDate(invitation.expiresAt)}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={(e) => onMenuOpen(e, invitation)} sx={{ color: dsVar('fg-2') }}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={onMenuClose}
        PaperProps={{
          sx: {
            bgcolor: dsVar('surface'),
            border: `1px solid ${dsVar('line')}`,
            color: dsVar('fg'),
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              py: 1,
              '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.05)', color: dsVar('accent-fg') }
            }
          }
        }}
      >
        {selectedInvitation && selectedInvitation.status === 'pending' && (
          <MenuItem onClick={() => onResend(selectedInvitation)}>
            <ListItemIcon sx={{ color: 'inherit' }}><Send fontSize="small" /></ListItemIcon>
            <ListItemText primary="Resend Invitation" />
          </MenuItem>
        )}
        {selectedInvitation && selectedInvitation.status === 'pending' && (
          <MenuItem onClick={() => onCancel(selectedInvitation)} sx={{ color: ink('#ef4444') }}>
            <ListItemIcon sx={{ color: 'inherit' }}><Cancel fontSize="small" /></ListItemIcon>
            <ListItemText primary="Cancel Invitation" />
          </MenuItem>
        )}
        <MenuItem onClick={onMenuClose}>
          <ListItemIcon sx={{ color: 'inherit' }}><Delete fontSize="small" /></ListItemIcon>
          <ListItemText primary="Delete Record" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Send Invitation Dialog Component
interface SendInvitationDialogProps {
  open: boolean;
  onClose: () => void;
  form: any;
  setForm: (form: any) => void;
  onSubmit: () => void;
  loading: boolean;
  availableGroups: any[];
  availableProjects: any[];
  loadingGroups: boolean;
  loadingProjects: boolean;
}

const SendInvitationDialog: React.FC<SendInvitationDialogProps> = ({
  open,
  onClose,
  form,
  setForm,
  onSubmit,
  loading,
  availableGroups,
  availableProjects,
  loadingGroups,
  loadingProjects
}) => {
  const handlePermissionChange = (permission: string, checked: boolean) => {
    if (checked) {
      setForm({ ...form, permissions: [...form.permissions, permission] });
    } else {
      setForm({ ...form, permissions: form.permissions.filter((p: string) => p !== permission) });
    }
  };

  const handleOrgPermissionChange = (permission: keyof typeof form.orgPermissions, checked: boolean) => {
    setForm({
      ...form,
      orgPermissions: {
        ...form.orgPermissions,
        [permission]: checked
      }
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: dsVar('page'),
          backgroundImage: 'none',
          borderRadius: '20px',
          border: `1px solid ${dsVar('line')}`,
          color: dsVar('fg'),
          boxShadow: '0 24px 48px rgba(0,0,0,0.8)'
        }
      }}
    >
      <DialogTitle sx={{ p: 4, pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: dsVar('accent-fg'), letterSpacing: '-0.02em' }}>
          Send New Invitation
        </Typography>
        <Typography variant="body2" sx={{ color: dsVar('fg-2'), mt: 1 }}>
          The invitee will receive an email with instructions to join the organization.
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 4, pt: 1 }}>
        <Grid container spacing={4} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Recipient Email Address"
              type="email"
              placeholder="e.g., colleague@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              InputProps={{
                sx: { 
                  color: dsVar('fg'), 
                  bgcolor: dsVar('surface'), 
                  borderRadius: '12px',
                  '& fieldset': { borderColor: dsVar('line') }
                }
              }}
              InputLabelProps={{ sx: { color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } } }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } }}>Assign Role</InputLabel>
              <Select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                label="Assign Role"
                sx={{
                  color: dsVar('fg'),
                  bgcolor: dsVar('surface'),
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') }
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: dsVar('surface'), color: dsVar('fg'), border: `1px solid ${dsVar('line')}` } } }}
              >
                <MenuItem value="user">User (Standard Access)</MenuItem>
                <MenuItem value="admin">Admin (Full Access)</MenuItem>
                <MenuItem value="manager">Manager (Group Access)</MenuItem>
                <MenuItem value="viewer">Viewer (Read Only)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } }}>Target User Group</InputLabel>
              <Select
                value={form.userGroupId}
                onChange={(e) => setForm({ ...form, userGroupId: e.target.value })}
                label="Target User Group"
                disabled={loadingGroups}
                sx={{
                  color: dsVar('fg'),
                  bgcolor: dsVar('surface'),
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') }
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: dsVar('surface'), color: dsVar('fg'), border: `1px solid ${dsVar('line')}` } } }}
              >
                <MenuItem value=""><em>None - Assign Later</em></MenuItem>
                {availableGroups.map((group) => (
                  <MenuItem key={group._id || group.id} value={group._id || group.id}>
                    {group.name || group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } }}>Initial Project Assignment</InputLabel>
              <Select
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                label="Initial Project Assignment"
                disabled={loadingProjects}
                sx={{
                  color: dsVar('fg'),
                  bgcolor: dsVar('surface'),
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') }
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: dsVar('surface'), color: dsVar('fg'), border: `1px solid ${dsVar('line')}` } } }}
              >
                <MenuItem value=""><em>None - Assign Later</em></MenuItem>
                {availableProjects.map((project) => (
                  <MenuItem key={project._id || project.id} value={project._id || project.id}>
                    {project.name || project.projectName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Organization"
              value={form.organisationName}
              disabled
              InputProps={{
                sx: { 
                  color: dsVar('fg-2'), 
                  bgcolor: dsVar('line', 0.3), 
                  borderRadius: '12px',
                  '& fieldset': { borderColor: `${dsVar('line')} !important` }
                }
              }}
              InputLabelProps={{ sx: { color: dsVar('fg-2') } }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Accordion sx={{ bgcolor: dsVar('line', 0.3), color: dsVar('fg'), borderRadius: '12px !important', border: `1px solid ${dsVar('line')}`, backgroundImage: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: dsVar('accent-fg') }} />}>
                <Typography sx={{ fontWeight: 700, color: dsVar('accent-fg') }}>Extended Permissions</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 3, pb: 3 }}>
                <Typography variant="body2" sx={{ color: dsVar('fg-2'), mb: 3 }}>
                  Define organization-wide capabilities. Note: More granular project permissions can be configured after the user joins.
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { key: 'canManageUsers', label: 'Manage All Organization Users' },
                    { key: 'canInviteCrossGroupUsers', label: 'Invite Users Across Groups' },
                    { key: 'canAddCrossGroupUsers', label: 'Directly Add Cross-Group Users' },
                    { key: 'canManageProjectUsers', label: 'Modify Project Member Roles' },
                    { key: 'canEditProjectSettings', label: 'Edit Core Project Metadata' }
                  ].map((perm) => (
                    <Grid item xs={12} md={6} key={perm.key}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={form.orgPermissions[perm.key as keyof typeof form.orgPermissions]}
                            onChange={(e) => handleOrgPermissionChange(perm.key as any, e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: dsVar('accent-fg') },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: dsVar('accent') }
                            }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ color: dsVar('fg') }}>{perm.label}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Personal Invitation Message"
              multiline
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Hi there! I'd like to invite you to join our team on DataScient..."
              InputProps={{
                sx: { 
                  color: dsVar('fg'), 
                  bgcolor: dsVar('surface'), 
                  borderRadius: '12px',
                  '& fieldset': { borderColor: dsVar('line') }
                }
              }}
              InputLabelProps={{ sx: { color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 4, pt: 0, gap: 2 }}>
        <Button 
          onClick={onClose} 
          sx={{ color: dsVar('fg-2'), textTransform: 'none', fontWeight: 600, '&:hover': { color: dsVar('fg') } }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={loading || !form.email}
          sx={{
            bgcolor: dsVar('accent'),
            color: dsVar('on-accent'),
            fontWeight: 800,
            px: 4,
            py: 1.2,
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': { bgcolor: dsVar('accent'), transform: 'translateY(-1px)' },
            boxShadow: '0 4px 14px 0 rgba(255, 222, 89, 0.2)',
            '&.Mui-disabled': { bgcolor: dsVar('line'), color: dsVar('fg-2') }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: dsVar('on-accent') }} /> : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitationManagement; 